import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { submit, reset } from 'redux-form';
import { useLocation, useHistory } from 'react-router';
import { createStructuredSelector } from 'reselect';
import { useInjectSaga } from '../../utils/injectSaga';
import { useInjectReducer } from '../../utils/injectReducer';
import saga from './saga';
import reducer from './reducer';
import OrderGroupInfo from './components/order-group-info';
import { ModalWindow } from '../../semantic-ui/components/modal-window';
import { Audit } from '../../components/audit';
import {
	getIsModalShownSelector,
	getActiveOrderGroupIdSelector,
	getDataNameSelector,
	getIsFormShownSelector,
	getIsOrderGroupSavingSelector,
	getIsOrderGroupInfoLoadingSelector,
	getIsOrderGroupDeletingSelector,
	getIsEditFormShownSelector,
	getIsTypesLoadingSelector,
	getOrderNameSelector,
	getIsConfigsLoadingSelector,
	getIsTransportTypesLoadingSelector,
	getIsOrdersGroupsTrashedSelector,
} from './selectors';
import { getIsAvailableOrderGroupsButtonsSelector } from '../profile/selectors';
import ModalFooterButtons from '../../semantic-ui/components/modal-window/modal-footer-buttons';
import { setWindowInfoAction } from '../alert-modal/actions';
import { OrderGroupForm } from './components/order-group-form';
import {
	getOrderGroupInfoAction,
	deleteOrderGroupAction,
	resetStateAction,
	createOrderGroupAction,
	getTypesAction,
	getModesAction,
	getPeriodsAction,
	updateOrderGroupAction,
	getConfigsAction,
	getTransportTypesAction,
	getTariffTypesAction,
	getAutoparksAction,
} from './actions';
import { FORM_NAME } from './constants';
import { removeModalQuery } from '../../utils/removeModalQuery';
import closeModal from '../../utils/closeModal';
import cancelModal from '../../utils/cancelModal';

const OrderGroupsModalWrapper = ({
	isOrderGroupInfoLoading,
	isOrderGroupSaving,
	isOrderGroupDeleting,
	onResetState,
	onDeleteOrderGroup,
	isModalShown,
	activeOrderGroupId,
	onCreateOrderGroup,
	isAvailable,
	onGetOrderGroupInfo,
	onSetWindowInfo,
	isFormShown,
	onGetTypes,
	onGetModes,
	onGetPeriods,
	onFormSubmit,
	isEditFormShown,
	onUpdateOrderGroup,
	isTypeLoading,
	orderName,
	onGetConfigs,
	onGetTransportTypes,
	isConfigsLoading,
	isTransportTypeLoading,
	isOrdersGroupsTrashed,
	onGetTariffTypes,
	onGetAutoparks,
}) => {
	if (!isModalShown) return null;
	useInjectSaga({ key: 'orderGroupsModalSaga', saga });
	useInjectReducer({ key: 'orderGroupsModalReducer', reducer });

	const { search, pathname } = useLocation();

	const history = useHistory();

	const query = new URLSearchParams(search);

	const isAuditTabShown = query.get('tab') === 'audit';

	useEffect(() => {
		if (activeOrderGroupId) {
			onGetOrderGroupInfo(activeOrderGroupId);
		}
	}, [activeOrderGroupId]);

	useEffect(() => {
		onGetTypes();
		onGetModes();
		onGetPeriods();
		onGetConfigs();
		onGetTransportTypes();
		onGetTariffTypes();
		onGetAutoparks();
	}, []);

	const handleModalCancel = () => {
		if (query.get('mode') === 'edit') {
			query.delete('mode');
		} else {
			cancelModal(query);
			onResetState();
		}
		history.push(`${pathname}?${query}`);
	};

	const handleModalClose = () => {
		closeModal(query);
		onResetState();
		history.push(`${pathname}?${query}`);
	};

	const handleNavItemClick = tab => {
		if (tab === 'info') {
			query.delete('tab');
		}
		if (tab === 'audit') {
			query.set('tab', 'audit');
		}
		history.push(`${pathname}?${query}`);
	};

	const navItems = useMemo(() => {
		if (isFormShown || isEditFormShown) {
			return [];
		}

		return [
			{
				id: 'info',
				text: 'Информация',
				isActive: !isAuditTabShown,
				onClick: () => handleNavItemClick('info'),
			},
			{
				id: 'audit',
				text: 'Изменения',
				isActive: isAuditTabShown,
				onClick: () => handleNavItemClick('audit'),
			},
		];
	}, [isFormShown, isAuditTabShown, handleNavItemClick]);

	const isOrderGroupFormOpen = useMemo(() => isFormShown || isEditFormShown, [isFormShown, isEditFormShown]);

	const modalContent = useMemo(() => {
		if (isAuditTabShown) {
			return <Audit entityName="orders_groups" entityId={activeOrderGroupId} />;
		}

		if (isOrderGroupFormOpen) {
			return <OrderGroupForm />;
		}

		return <OrderGroupInfo />;
	}, [isAuditTabShown, activeOrderGroupId, isOrderGroupFormOpen]);

	const handleOrderGroupDelete = () => {
		onSetWindowInfo({
			type: 'delete',
			title: 'Вы уверены?',
			text: 'Вы не сможете это отменить',
			button: {
				type: 'success',
				text: 'Да, удалить',
				onClick: () =>
					onDeleteOrderGroup({
						id: activeOrderGroupId,
						redirect: handleModalClose,
						searchQuery: removeModalQuery(query.toString()),
						pathname,
					}),
			},
		});
	};

	const handleOrderGroupFormSubmit = async () => {
		await onFormSubmit(FORM_NAME);
		if (activeOrderGroupId) {
			onUpdateOrderGroup({
				id: activeOrderGroupId,
				redirect: handleModalCancel,
				searchQuery: removeModalQuery(query.toString()),
				pathname,
			});
			return;
		}
		onCreateOrderGroup({
			redirect: handleModalClose,
			searchQuery: removeModalQuery(query.toString()),
		});
	};

	const handleKeyDown = e => {
		if (e.key === 'Enter' && e.shiftKey === false) {
			handleOrderGroupFormSubmit();
		}
	};

	const handleOpenEditForm = () => {
		query.delete('mode');
		query.delete('tab');
		query.set('mode', 'edit');
		history.push(`${pathname}?${query}`);
	};

	const isSaveButtonDisabled = useMemo(() => {
		return (
			isOrderGroupSaving || isOrderGroupInfoLoading || isTypeLoading || isConfigsLoading || isTransportTypeLoading
		);
	}, [isOrderGroupSaving, isOrderGroupInfoLoading, isTypeLoading, isConfigsLoading, isTransportTypeLoading]);

	const rightButtons = useMemo(() => {
		if (isFormShown || (isEditFormShown && isAvailable.create)) {
			return [
				{
					onClick: handleOrderGroupFormSubmit,
					text: 'Сохранить',
					color: 'primary',
					loading: isOrderGroupSaving,
					disabled: isSaveButtonDisabled,
				},
				{
					onClick: handleModalCancel,
					text: 'Отмена',
					color: 'secondary',
					disabled: isOrderGroupSaving,
				},
			];
		}

		const buttons = [];
		if (isAvailable.update && !isEditFormShown && !isAuditTabShown && !isOrdersGroupsTrashed) {
			buttons.push({
				onClick: handleOpenEditForm,
				text: 'Изменить',
				color: 'primary',
				disabled: isOrderGroupInfoLoading || isOrderGroupDeleting,
			});
		}

		buttons.push({
			onClick: handleModalClose,
			text: 'Закрыть',
			color: 'secondary',
		});

		return buttons;
	}, [
		isFormShown,
		query,
		isAvailable.update,
		isOrderGroupSaving,
		isOrderGroupInfoLoading,
		isEditFormShown,
		isAuditTabShown,
	]);

	const leftButtons = useMemo(() => {
		if (isAvailable.delete && !isFormShown && !isEditFormShown && !isAuditTabShown && !isOrdersGroupsTrashed) {
			return [
				{
					onClick: handleOrderGroupDelete,
					text: 'Удалить',
					color: 'warning',
					disabled: isOrderGroupInfoLoading || isOrderGroupDeleting,
				},
			];
		}

		return [];
	}, [
		isFormShown,
		isAvailable.delete,
		isOrderGroupInfoLoading,
		isOrderGroupDeleting,
		isEditFormShown,
		isAuditTabShown,
	]);

	const headerText = useMemo(() => {
		if (isEditFormShown) return 'Изменить группировку';
		return isFormShown ? 'Создать группировку' : orderName;
	}, [isFormShown, orderName, isEditFormShown]);

	return (
		<ModalWindow
			isModalShown={isModalShown}
			onKeyDown={handleKeyDown}
			onClose={handleModalClose}
			headerText={headerText}
			navItems={navItems}
			modalContent={modalContent}
			style={{ width: '895px' }}
			actionButtons={[{ type: 'close', onClick: handleModalClose, text: 'order-group' }]}
			footerButtons={<ModalFooterButtons leftButtons={leftButtons} rightButtons={rightButtons} />}
		/>
	);
};

OrderGroupsModalWrapper.propTypes = {
	isOrderGroupInfoLoading: PropTypes.bool,
	isOrderGroupSaving: PropTypes.bool,
	isOrderGroupDeleting: PropTypes.bool,
	onDeleteOrderGroup: PropTypes.func.isRequired,
	onResetState: PropTypes.func.isRequired,
	isModalShown: PropTypes.bool.isRequired,
	onCreateOrderGroup: PropTypes.func.isRequired,
	activeOrderGroupId: PropTypes.string,
	onGetOrderGroupInfo: PropTypes.func.isRequired,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
	onSetWindowInfo: PropTypes.func.isRequired,
	isFormShown: PropTypes.bool.isRequired,
	onGetTypes: PropTypes.func.isRequired,
	onGetModes: PropTypes.func.isRequired,
	onGetPeriods: PropTypes.func.isRequired,
	onFormSubmit: PropTypes.func.isRequired,
	isEditFormShown: PropTypes.bool.isRequired,
	onUpdateOrderGroup: PropTypes.func.isRequired,
	isTypeLoading: PropTypes.bool,
	orderName: PropTypes.string.isRequired,
	onGetConfigs: PropTypes.func.isRequired,
	onGetTransportTypes: PropTypes.func.isRequired,
	isConfigsLoading: PropTypes.bool,
	isTransportTypeLoading: PropTypes.bool,
	isOrdersGroupsTrashed: PropTypes.bool,
	onGetTariffTypes: PropTypes.func.isRequired,
	onGetAutoparks: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
	isOrderGroupInfoLoading: getIsOrderGroupInfoLoadingSelector(),
	isOrderGroupSaving: getIsOrderGroupSavingSelector(),
	isOrderGroupDeleting: getIsOrderGroupDeletingSelector(),
	isModalShown: getIsModalShownSelector(),
	activeOrderGroupId: getActiveOrderGroupIdSelector(),
	dataName: getDataNameSelector(),
	isAvailable: getIsAvailableOrderGroupsButtonsSelector(),
	isFormShown: getIsFormShownSelector(),
	isEditFormShown: getIsEditFormShownSelector(),
	isTypeLoading: getIsTypesLoadingSelector(),
	orderName: getOrderNameSelector(),
	isConfigsLoading: getIsConfigsLoadingSelector(),
	isTransportTypeLoading: getIsTransportTypesLoadingSelector(),
	isOrdersGroupsTrashed: getIsOrdersGroupsTrashedSelector(),
});

const mapDispatchToProps = {
	onGetOrderGroupInfo: getOrderGroupInfoAction,
	onDeleteOrderGroup: deleteOrderGroupAction,
	onResetState: resetStateAction,
	onCreateOrderGroup: createOrderGroupAction,
	onSetWindowInfo: setWindowInfoAction,
	onGetTypes: getTypesAction,
	onGetModes: getModesAction,
	onGetPeriods: getPeriodsAction,
	onFormSubmit: submit,
	resetForm: reset,
	onUpdateOrderGroup: updateOrderGroupAction,
	onGetConfigs: getConfigsAction,
	onGetTransportTypes: getTransportTypesAction,
	onGetTariffTypes: getTariffTypesAction,
	onGetAutoparks: getAutoparksAction,
};

const OrderGroupsModal = connect(mapStateToProps, mapDispatchToProps)(OrderGroupsModalWrapper);

export default OrderGroupsModal;
